import { ReactNode, useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { sp } from '@/theme/mui';

import { ShortlistedCareHomeCard } from '@/components/molecules/ShortlistedCareHomeCard/ShortlistedCareHomeCard';
import { Card } from '@/components/atoms/Card/Card';
import { Label } from '@/components/atoms/Label/Label';
import { Title } from '@/components/atoms/Title/Title';
import { CardContainer } from '@/components/templates/CardContainer/CardContainer';
import { useWindowSizeBreakpoints } from '@/hooks';

import {
  CareHomeAddress,
  CareHomeProviderData,
  CareHomeShortlist,
  CareHomeShortlistData,
  ProviderType,
} from '@karehero/models';

export const CareHomeOverview = ({
  shortlist,
  shortlistData,
  onMarkSecured,
  headerActions,
}: CareHomeOverviewProps) => {
  const { isLg, isXl } = useWindowSizeBreakpoints();

  const formatAddress = useMemo(
    () =>
      (address: CareHomeAddress): string => {
        const parts = [
          address.streetAddress1,
          address.city,
          address.district,
          address.county,
          address.region,
          address.country,
          address.postcode,
        ].filter(Boolean);
        return parts.join(', ');
      },
    [],
  );

  return (
    <Stack
      sx={{
        pb: sp[8],
        px: sp[4],
        overflowX: 'visible',
      }}
    >
      <Stack>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Title isNoMargin size={'sm'}>
            Your Shortlist
          </Title>
          {headerActions}
        </Box>
        {shortlistData.items && (
          <Box sx={{ my: sp[24] }}>
            <Card color={'yellow'} isBordered={false} elevation={0} isPaddingSm>
              <Box sx={{ typography: 'bodyMediumWeight' }}>
                <Label
                  id='shortlist-label'
                  text='Your Care Expert has chosen the below care services to match your care needs.'
                />
              </Box>
            </Card>
          </Box>
        )}
      </Stack>
      <CardContainer maxColumns={isXl ? 3 : isLg ? 2 : 1}>
        {shortlistData.items && shortlistData.items.length > 0 ? (
          shortlistData.items.map((item: CareHomeProviderData) => (
            <ShortlistedCareHomeCard
              key={item.regulatoryID}
              regulatoryID={item.regulatoryID}
              headerImageUrl={item.photos?.medium}
              name={item.name}
              address={
                item.address
                  ? formatAddress(item.address)
                  : 'Address not available'
              }
              rating={
                item.rating ? item.rating.overallRating : 'No rating available'
              }
              phone={item.enquiryPhone}
              provider={item as ProviderType}
              available={item.availability}
              notes={item.notes}
              isSecured={item.regulatoryID === shortlist.securedRegulatoryID}
              onMarkSecured={() =>
                item.regulatoryID && onMarkSecured?.(item.regulatoryID)
              }
            />
          ))
        ) : (
          <Box sx={{ typography: 'bodyMd', color: '#404042' }}>
            No care homes available in your shortlist.
          </Box>
        )}
      </CardContainer>
    </Stack>
  );
};

interface CareHomeOverviewProps {
  shortlist: CareHomeShortlist;
  shortlistData: CareHomeShortlistData;
  onMarkSecured?: (regulatoryID: string) => void;
  headerActions?: ReactNode;
}

CareHomeOverview.displayName = 'CareHomeOverview';

export default CareHomeOverview;
